import { type NavbarTabType } from "@indigo-components";
import { type ReactNode } from "react";
import { AuthUserGroup, isHistorianUserGroup } from "~/types/auth";

interface GetNavbarTabsOptions {
  userGroup: AuthUserGroup | null;
  isLibraryEnabled: boolean;
  isAllCasesDashboardEnabled: boolean;
  isReferralEnabled: boolean;
  libraryTag?: ReactNode;
}

export const getNavbarTabs = ({
  userGroup,
  isLibraryEnabled,
  isAllCasesDashboardEnabled,
  isReferralEnabled,
  libraryTag,
}: GetNavbarTabsOptions): NavbarTabType[] => {
  let tabs: NavbarTabType[] = [];

  if (userGroup === AuthUserGroup.DOCTOR) {
    tabs = [
      {
        to: "/today-visits",
        title: "Today",
        iconName: "today-visits-tab-icon",
      },
      {
        to: "/upcoming-appointments",
        title: "Upcoming",
        iconName: "appointments-tab",
      },
      {
        to: "/active-cases",
        title: "Active Cases",
        iconName: "active-cases-tab",
      },
      {
        to: "/past-cases",
        title: "Past Cases",
        iconName: "past-cases-tab",
      },
      {
        to: "/appointments",
        title: "Appointments",
        iconName: "all-appointments-tab-icon",
      },
      // Library + Referral share a separated group; only show the separator
      // when at least one of them is enabled.
      ...(isLibraryEnabled || isReferralEnabled
        ? [{ type: "separator" as const }]
        : []),
      ...(isLibraryEnabled
        ? [
            {
              to: "/library",
              title: "Library",
              iconName: "learning-tab-icon" as const,
              tag: libraryTag,
            },
          ]
        : []),
      // Referral is physician-only, behind the referral flag.
      ...(isReferralEnabled
        ? [
            {
              to: "/referral",
              title: "Referral",
              iconName: "bonus" as const,
            },
          ]
        : []),
    ];
  } else if (isHistorianUserGroup(userGroup)) {
    tabs = [
      {
        id: "previsits-parent",
        to: "/in-person-evaluations",
        title: "Pre-visits",
        iconName: "previsit-appointments-tab-icon",
      },
      {
        id: "previsits-in-person-evals",
        parentId: "previsits-parent",
        to: "/in-person-evaluations",
        title: "In-person evals",
        expandedOnly: true,
      },
      {
        id: "previsits-supplementals",
        parentId: "previsits-parent",
        to: "/supplementals",
        title: "Supplementals",
        expandedOnly: true,
      },
    ];
  } else if (
    userGroup === AuthUserGroup.MEDICAL_ASSISTANT ||
    userGroup === AuthUserGroup.ADMIN
  ) {
    tabs = [
      {
        to: "/appointments/new",
        title: "New appointment",
        iconName: "calendar-plus",
        external: true,
      },
      {
        type: "separator",
      },
      {
        to: "/appointments",
        title: "All appointments",
        iconName: "all-appointments-tab-icon",
      },
      {
        to: "/bookings",
        title: "Bookings",
        iconName: "appointments-tab",
      },
      {
        id: "previsits-parent",
        to: "/in-person-evaluations",
        title: "Pre-visits",
        iconName: "previsit-appointments-tab-icon",
      },
      {
        id: "previsits-in-person-evals",
        parentId: "previsits-parent",
        to: "/in-person-evaluations",
        title: "In-person evals",
        expandedOnly: true,
      },
      {
        id: "previsits-supplementals",
        parentId: "previsits-parent",
        to: "/supplementals",
        title: "Supplementals",
        expandedOnly: true,
      },
      {
        id: "previsits-depositions",
        parentId: "previsits-parent",
        to: "/depositions",
        title: "Depositions",
        expandedOnly: true,
      },
      {
        to: "/today-visits",
        title: "Today",
        iconName: "today-visits-tab-icon",
      },
      {
        to: "/active-visits",
        title: "Active visit",
        iconName: "active-cases-tab",
      },
      {
        to: "/billing-and-collection",
        title: "Billing & collection",
        iconName: "billing-tab-icon",
      },
      ...(isAllCasesDashboardEnabled
        ? [
            { type: "separator" as const },
            {
              to: "/all-cases",
              title: "All cases",
              iconName: "folder" as const,
            },
          ]
        : []),
      ...(isLibraryEnabled
        ? [
            { type: "separator" as const },
            {
              to: "/library",
              title: "Library",
              iconName: "learning-tab-icon" as const,
              tag: libraryTag,
            },
          ]
        : []),
    ];
  }

  return tabs;
};
